import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, catchError, combineLatest, concatMap, debounceTime, filter, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { ContactSource, PortalApiService, UtmDetails } from 'src/app/services/portal-api.service';
import { AuthService } from './auth.service';


export interface UtmInfo {
  anonymousId: string | undefined;
  campaignName: string | undefined;
  source: ContactSource;
}

declare var Cookiebot: any;

@Injectable({
  providedIn: 'root'
})
export class UtmTrackerService {

  public utmCampaign: string = '';
  public utmSource: ContactSource = ContactSource.Other;
  public utmDetails: UtmDetails = new UtmDetails();
  private cookieConsent$ = new BehaviorSubject<boolean>(false);

  constructor(    
    private route: ActivatedRoute,
    private router: Router,
    private portalApi: PortalApiService,
    private authService: AuthService,
    private cookieService: CookieService) { }

  init() {
    this.initCookieConsentListener();
    
    combineLatest([this.route.queryParams, this.router.events.pipe(filter(event => event instanceof NavigationEnd), map(() => this.router.url)), this.cookieConsent$])
    .pipe(
      debounceTime(1000),
      filter(([params, url, cookieConsent]) => {
        const utmDataRaw = this.cookieService.get('utm_info');
        const hasUtmCookie = !!utmDataRaw;
        const hasUtmQueryParams = !!params['utm_source'] && !!params['utm_campaign'];
        const notOnLoginPage = !url.endsWith('/login');
        const notOnAuthCallback = !url.includes('/auth-callback');
        return cookieConsent && notOnLoginPage && notOnAuthCallback && (hasUtmQueryParams || hasUtmCookie);
      }),
      concatMap(([params, _, __]) => {
        return this.authService.isLoggedIn().pipe(map(isLoggedIn => ({ params, isLoggedIn })))
      }),
      concatMap(({ params, isLoggedIn }) => {
        const utmDataRaw = this.cookieService.get('utm_info');
        const utmData: UtmInfo = utmDataRaw ? JSON.parse(utmDataRaw) : null;

        const hasParams = !!params['utm_source'] && !!params['utm_campaign'];
        const isValidSource = hasParams && Object.values(ContactSource).includes(params['utm_source'] as ContactSource);
        this.utmSource = isValidSource ? params['utm_source'] : ContactSource.Other;
        this.utmCampaign = params['utm_campaign'];

        // # Case 1: 
        // Existing cookie and params — compare and maybe update existing anonymous account
        // or update existing registered account.
        if (utmData && hasParams) {
          const sameCampaign = utmData.campaignName === this.utmCampaign;
          const sameSource = utmData.source === this.utmSource;

          if (!sameCampaign || !sameSource) {
            this.utmDetails = new UtmDetails({
              anonymousId: utmData.anonymousId,
              campaign: this.utmCampaign,
              source: this.utmSource
            });
            
            return isLoggedIn
              ? this.portalApi.updateAccountOwnerContact(this.utmDetails).pipe(
                  tap(() => {
                    this.cookieService.delete('utm_info', '/');
                    this.cleanupUtmParams();            
                  })
                )
              : this.portalApi.createOrUpdateAnonymousAccount(this.utmDetails).pipe(
                  tap(() => {
                    this.cookieService.delete('utm_info', '/');
                    this.cleanupUtmParams();            
                  })
                );
          } 
        }

        // # Case 2: 
        // Existing cookie, with or without params but logged in — update existing registered account.
        if (utmData && isLoggedIn) {
          this.utmDetails =  new UtmDetails({
              anonymousId: utmData.anonymousId,
              campaign: utmData.campaignName,
              source: utmData.source
            });

          return this.portalApi.updateAccountOwnerContact(this.utmDetails).pipe(
            tap(() => {
              this.cookieService.delete('utm_info', '/');
              this.cleanupUtmParams();            
            })
          )
        }

        // # Case 3: 
        // No cookie, but params — create or update based on login
        if (!utmData && hasParams) {
          this.utmDetails = new UtmDetails({
            campaign: this.utmCampaign,
            source: this.utmSource
          });

          return isLoggedIn
            ? this.portalApi.updateAccountOwnerContact(this.utmDetails).pipe(
                tap(() => {
                  this.cleanupUtmParams();            
                })
              )
            : this.portalApi.createOrUpdateAnonymousAccount(this.utmDetails).pipe(
                tap(() => {
                  this.cleanupUtmParams();            
                })
              );
        }

        this.cleanupUtmParams();
        return of();
      }),
      catchError((error) => {
        console.log(error)
        return of();
      }),
      tap(response => {
        // # If the response is a string, it represents the OwnerId of the newly created anonymous user
        // and creates a cookie with relevant info
        if (typeof response === 'string' && response.trim() !== '') {
          const utmData: UtmInfo = {
            anonymousId: response,
            campaignName: this.utmCampaign,
            source: this.utmSource
          };
          this.cookieService.set('utm_info', JSON.stringify(utmData), 14, '/');
        }
      })
    ).subscribe();
  }
  
  private cleanupUtmParams(): void {
    this.router.navigate([], {
      queryParams: {
        utm_source: null,
        utm_medium: null,
        utm_campaign: null,
        utm_content: null,
        fbclid: null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private initCookieConsentListener() {
    window.addEventListener('CookiebotOnAccept', () => {
      const hasConsent = Cookiebot?.consent?.marketing === true;
      this.cookieConsent$.next(hasConsent);
    });

    // # Check if consent already exists
    if (typeof Cookiebot !== 'undefined' && Cookiebot.consents) {
      const hasConsent = Cookiebot.consent.marketing === true;
      this.cookieConsent$.next(hasConsent);
    }

    // ## Uncomment if you want to debug locally
    // this.cookieConsent$.next(true);
  }
}
